/**
 * Actor-Gate für sensible Domain-Writes (RL14).
 *
 * UI-Gates (ProtectedRoute, Nav, Buttons) bleiben die erste Verteidigungslinie, sind
 * aber nicht die einzige Wahrheit: wer den Service direkt aufruft (DevTools, ein
 * verwaister Handler) umgeht sie sonst vollständig. Der Guard liest die Rolle aus dem
 * gleichen KV-Key wie RoleContext, damit UI und Domain nicht auseinanderlaufen.
 *
 * Fail-open bei *unbekannter* Rolle ist Absicht: Boot-Migrationen, Tests und
 * Admin-Skripte laufen ohne Session und dürfen nicht blockiert werden. Sobald eine
 * Worker-Rolle aktiv ist, entscheidet ausschließlich deren Permission-Set.
 */

import { loadRoleForBusiness } from '../../utils/roleStorage';
import { getCurrentBusinessId } from '../../utils/sharedCore';
import { normalizeWorkerPermissionsForEnforce } from '../../utils/workerPermissions';

/** @returns {{ type?: string, id?: *, permissions?: Record<string, boolean> }|null} */
export function readActorRole(businessId = null) {
  try {
    const bid = businessId || getCurrentBusinessId({ allowFallback: false });
    if (!bid) return null;
    return loadRoleForBusiness(bid).role || null;
  } catch (_) {
    return null;
  }
}

function permissionKeys(permission) {
  if (Array.isArray(permission)) return permission.filter(Boolean);
  return permission ? [permission] : [];
}

/**
 * @param {string|string[]} permission Key(s) aus WORKER_PAGE_PERMISSION_KEYS — Array = any-of
 * @param {{ businessId?: string|null, role?: object|null }} [options]
 * @returns {{ allowed: boolean, reason?: string, actor?: object|null, permission?: string }}
 */
export function checkActorPermission(permission, options = {}) {
  const role = options.role !== undefined ? options.role : readActorRole(options.businessId);
  const keys = permissionKeys(permission);

  // No session in scope (boot, tests, admin tooling) — nothing to authorize against.
  if (!role || !role.type) return { allowed: true, actor: role || null };
  if (role.type === 'manager' || role.type === 'admin') return { allowed: true, actor: role };
  if (role.type !== 'worker') return { allowed: true, actor: role };

  const perms = normalizeWorkerPermissionsForEnforce(role.permissions);
  if (keys.some((key) => perms[key])) return { allowed: true, actor: role };

  return {
    allowed: false,
    reason: 'actor_permission_denied',
    actor: role,
    permission: keys[0] || null
  };
}

/**
 * Guard für Services, die ein Ergebnisobjekt zurückgeben (kein throw).
 *
 * @returns {null|{ success: false, error: string, permission: string|null, action: string }}
 *   null = erlaubt; sonst das Fehlerergebnis, das der Service zurückgeben soll.
 */
export function denyActor(permission, action, options = {}) {
  const result = checkActorPermission(permission, options);
  if (result.allowed) return null;

  const keys = permissionKeys(permission);
  console.warn(
    `[actorGuard] ${action} blocked — worker lacks "${keys.join('|')}"`,
    result.actor?.id ?? null
  );
  return {
    success: false,
    error: 'actor_permission_denied',
    permission: result.permission ?? keys[0] ?? null,
    action
  };
}

/** Guard für Aufrufer, die Exceptions erwarten (Persist-/Infra-Writes). */
export function assertActorPermission(permission, action, options = {}) {
  const denial = denyActor(permission, action, options);
  if (denial) {
    const keys = permissionKeys(permission);
    const error = new Error(`${action}: actor lacks permission "${keys.join('|')}"`);
    error.code = 'actor_permission_denied';
    error.permission = denial.permission;
    throw error;
  }
}
